import { useEffect, useState, useCallback } from "react";
import "swiper/css";
import "swiper/css/navigation";
import location from "../../../assets/location.svg";
import icon from "../../../assets/icon.svg";
import icon1 from "../../../assets/icon1.svg";
import squareMeters from "../../../assets/SquareMeters.svg";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import axiosInstance from "../../../apiInstances/axiosInstance";
import { Link, useNavigate } from "react-router-dom";
import axiosInstanceAuth from "../../../apiInstances/axiosInstanceAuth";
import HousePropertySlider from "./HousePropertySlider";
import { FaAngleRight, FaArrowRight } from "react-icons/fa";
import { Home } from "lucide-react";

/* ---------------- Skeleton ---------------- */

function HousePropertySkeleton() {
  return (
    <div className="bg-white rounded-2xl overflow-hidden border border-gray-200 animate-pulse w-full">
      <div className="w-full h-52 sm:h-56 md:h-48 lg:h-52 bg-gray-200" />

      <div className="p-5 flex flex-col">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-2">
            <div className="h-6 w-24 bg-gray-200 rounded" />
            <div className="h-5 w-40 bg-gray-200 rounded" />
          </div>
          <div className="w-10 h-10 rounded-full bg-gray-200" />
        </div>

        <div className="h-4 w-2/3 bg-gray-200 rounded mt-4" />
        <div className="border-t my-4" />

        <div className="flex items-center justify-between">
          <div className="h-4 w-16 bg-gray-200 rounded" />
          <div className="h-4 w-16 bg-gray-200 rounded" />
          <div className="h-4 w-16 bg-gray-200 rounded" />
        </div>
      </div>
    </div>
  );
}

const PropertyEmptyState = () => (
  <div className="w-full bg-white border-[1.5px] border-[#EAEAEA] rounded-2xl flex flex-col items-center justify-center text-center px-6 py-10 sm:px-10 sm:py-12 min-h-[260px] shadow-sm">
    <div
      className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full bg-[#F3F6F9] flex items-center justify-center mb-5 ring-1 ring-[#EAEAEA]"
      aria-hidden
    >
      <Home className="text-[#097FE6]" size={36} strokeWidth={1.75} />
    </div>
    <h3 className="text-lg sm:text-xl font-semibold text-[#000929] mb-2 max-w-md">
      No properties to show yet
    </h3>
    <p className="text-sm sm:text-base text-gray-500 max-w-lg mb-6 leading-relaxed">
      We couldn&apos;t find listings for this section right now. Browse the marketplace to discover available properties.
    </p>
    <Link to="/buy" className="w-full sm:w-auto">
      <button
        type="button"
        className="w-full sm:w-auto rounded-full px-6 sm:px-8 py-2.5 sm:py-3 font-medium text-sm sm:text-base bg-[#097FE6] text-white hover:opacity-95 transition-opacity"
      >
        Browse properties
      </button>
    </Link>
  </div>
);

const PropertyErrorState = ({ onRetry }) => (
  <div className="w-full bg-white border-[1.5px] border-[#EAEAEA] rounded-2xl flex flex-col items-center justify-center text-center px-6 py-10 sm:px-10 sm:py-12 min-h-[260px] shadow-sm">
    <div
      className="w-16 h-16 sm:w-[72px] sm:h-[72px] rounded-full bg-[#FEF2F2] flex items-center justify-center mb-5 ring-1 ring-[#FECACA]"
      aria-hidden
    >
      <span className="text-2xl sm:text-3xl font-semibold text-[#DC2626]" aria-hidden>
        !
      </span>
    </div>
    <h3 className="text-lg sm:text-xl font-semibold text-[#000929] mb-2 max-w-md">
      Couldn&apos;t load properties
    </h3>
    <p className="text-sm sm:text-base text-gray-500 max-w-lg mb-6 leading-relaxed">
      Something went wrong while fetching the list. Check your connection and try again.
    </p>
    <button
      type="button"
      onClick={onRetry}
      className="w-full sm:w-auto rounded-full px-6 sm:px-8 py-2.5 sm:py-3 font-medium text-sm sm:text-base bg-[#097FE6] text-white hover:opacity-95 transition-opacity"
    >
      Try again
    </button>
  </div>
);

/* ---------------- Main Component ---------------- */

const Housecart = () => {
  const navigate = useNavigate();
  const [clientSliderData, setClientSliderData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [progress, setProgress] = useState(0);


  /* -------- Fetch Properties -------- */

  const loadProperties = useCallback(async () => {
    try {
      setLoading(true);
      setFetchError(false);
      const userData = JSON.parse(localStorage.getItem("userData") || "null");
      const res = await axiosInstance.post(`/properties`, { userId: userData?.id || null });
      const raw = res?.data?.data;
      const myData = Array.isArray(raw) ? raw : [];
      if (myData?.length > 0) {
        const arrData = myData.map((e) => {
          const squareMetersDetail = e?.type?.startsWith("House")
            ? e?.houseSize
            : e?.landSize;

          return {
            id: e?._id,
            clientImage: e?.image,
            head: e?.heading,
            like: true,
            locations: location,
            place: e?.address,
            price: e?.price,
            discription: e?.discription,
            collections: e?.collections ?? [],
            iconWithCount: [
              { icon: icon1, text: "Beds", count: e?.Bedrooms },
              { icon: icon, text: "Bathrooms", count: e?.Bathrooms },
              { icon: squareMeters, text: "m²", count: squareMetersDetail },
            ],
          };
        });
        setClientSliderData(arrData);
      } else {
        setClientSliderData([]);
        setFetchError(false);
        setLoading(false);
      }
    } catch (error) {
      console.log(error);
      setFetchError(true);
      setClientSliderData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProperties();
  }, [loadProperties]);

  const savedLocalPost = localStorage.getItem("Saved")?.split(",");

  /* ---------------- JSX ---------------- */

  return (
    <div className="bg-[#F3F6F9] py-12 sm:py-16 md:py-20 lg:py-24 xl:py-28">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* -------- Header -------- */}

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-12">
          <div className="max-w-xl">
            <h2 className="font-bold text-2xl sm:text-3xl lg:text-4xl text-[#000929] leading-tight">
              Based on your location
            </h2>
            <p className="text-sm sm:text-base text-gray-500 mt-3">
              Some of our picked properties near your location.
            </p>
          </div>

          <Link to="/buy">
            <button className="bg-[#097FE6] text-white text-sm sm:text-base rounded-full py-3 px-6 flex items-center gap-2 font-semibold whitespace-nowrap hover:scale-105 transition">
              Browse more properties
              <FaAngleRight />
            </button>
          </Link>
        </div>

        {/* -------- Mobile Layout -------- */}

        <div className="md:hidden flex flex-col gap-6">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <HousePropertySkeleton key={i} />
            ))
          ) : fetchError ? (
            <PropertyErrorState onRetry={loadProperties} />
          ) : clientSliderData?.length === 0 ? (
            <PropertyEmptyState />
          ) : (
            clientSliderData?.map((e, i) => (
              <HousePropertySlider
                key={e?.id || i}
                e={e}
                savedLocalPost={savedLocalPost}
              />
            ))
          )}

          {!loading && !fetchError && clientSliderData.length > 10 && (
            <div
              className="flex justify-center pt-4"
              onClick={() => navigate("/buy")}
            >
              <div className="bg-[#097FE6] text-white px-6 py-3 rounded-full flex items-center gap-2 cursor-pointer font-medium hover:scale-105 transition">
                Show all the property
                <FaAngleRight />
              </div>
            </div>
          )}
        </div>

        {/* -------- Desktop Swiper -------- */}
        {
          clientSliderData?.length === 0 ?
            <PropertyEmptyState />
            :
            <div className="hidden md:block">
              <Swiper
                modules={[Navigation]}
                navigation={{
                  prevEl: ".custom-prev",
                  nextEl: ".custom-next",
                }}
                spaceBetween={20}
                slidesPerView={1}
                onSlideChange={(swiper) => {
                  const totalSlides = swiper.slides.length;
                  const visibleSlides = swiper.params.slidesPerView || 1;
                  const maxIndex = totalSlides - visibleSlides;

                  if (maxIndex <= 0) {
                    setProgress(100);
                    return;
                  }

                  const percentage = (swiper.activeIndex / maxIndex) * 100;
                  setProgress(percentage);
                }}
                breakpoints={{
                  0: { slidesPerView: 1.1, spaceBetween: 16 },
                  640: { slidesPerView: 2, spaceBetween: 20 },
                  1024: { slidesPerView: 3, spaceBetween: 20 },
                  1440: { slidesPerView: 4, spaceBetween: 24 },
                }}
              >
                {loading
                  ? Array.from({ length: 4 }).map((_, i) => (
                    <SwiperSlide key={i}>
                      <HousePropertySkeleton />
                    </SwiperSlide>
                  ))
                  : clientSliderData?.map((e, i) => (
                    <SwiperSlide key={i}>
                      <HousePropertySlider
                        e={e}
                        savedLocalPost={savedLocalPost}
                      />
                    </SwiperSlide>
                  ))}

                {/* {clientSliderData.length > 10 && (
              <SwiperSlide>
                <div className="flex items-center justify-center h-full">
                  <div
                    onClick={() => navigate("/buy")}
                    className="bg-blue-100 px-6 py-3 rounded-full cursor-pointer flex items-center gap-3 hover:scale-105 transition"
                  >
                    <span className="text-sm font-medium">See More</span>
                    <div className="p-2 rounded-full bg-blue-500">
                      <FaAngleRight color="white" />
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            )} */}
              </Swiper>

              {/* -------- Progress + Navigation -------- */}

              <div className="hidden md:flex flex-col lg:flex-row justify-between items-center gap-6 mt-10">
                <div className="relative w-full lg:flex-1">
                  <div className="w-full h-[2px] bg-gray-300" />
                  <div
                    className="absolute top-1/2 -translate-y-1/2 left-0 bg-blue-500 rounded-xl transition-all duration-300"
                    style={{
                      width: `${progress}%`,
                      height: "4px",
                    }}
                  />
                </div>

                <div className="flex items-center gap-4">
                  <button className="custom-prev w-10 h-10 rounded-full border flex items-center justify-center bg-white shadow">
                    <FaArrowRight className="rotate-180 text-[#097FE6]" />
                  </button>
                  <button className="custom-next w-10 h-10 rounded-full border flex items-center justify-center bg-white shadow">
                    <FaArrowRight className="text-[#097FE6]" />
                  </button>
                </div>
              </div>
            </div>
        }
      </div>
    </div>
  );
};

export default Housecart;
